import React, { useState, useEffect } from 'react';
import KendoReactColorGradient from '@Shared.Angular/kendo.react/KendoReactColorGradient';

type Props = {
  colors: string[];
  selectedColor: string;
  onSelectColor: (color: string) => void;
  onValidityChange?: (isValid: boolean) => void;
};

const HEX_COLOR_PATTERN = /^#([A-Fa-f0-9]{3}|[A-Fa-f0-9]{6})$/;
const INVALID_HEX_COLOR_MESSAGE =
  'Invalid colour code. Please enter a valid HEX value (e.g. #FFF or #FFFFFF).';

const isCompleteHexInput = (color: string): boolean =>
  color.length === 4 || color.length === 7;

const normalizeHexColor = (color: string): string | null => {
  const compactColor = color.replace(/\s+/g, '').toUpperCase();
  const colorWithHash = compactColor.startsWith('#')
    ? compactColor
    : `#${compactColor}`;

  if (!HEX_COLOR_PATTERN.test(colorWithHash)) {
    return null;
  }

  if (colorWithHash.length === 4) {
    return `${colorWithHash[1]}${colorWithHash[1]}${colorWithHash[2]}${colorWithHash[2]}${colorWithHash[3]}${colorWithHash[3]}`;
  }

  return colorWithHash.substring(1);
};

const formatColorInput = (color: string): string => {
  const normalizedColor = normalizeHexColor(color);
  return normalizedColor ? `#${normalizedColor}` : '';
};

export const TagColorGradient = ({
  colors,
  selectedColor,
  onSelectColor,
  onValidityChange
}: Props) => {
  const [showGradient, setShowGradient] = useState(false);
  const [hexColorInput, setHexColorInput] = useState(() =>
    formatColorInput(selectedColor)
  );
  const [hexColorError, setHexColorError] = useState('');

  useEffect(() => {
    const formattedColor = formatColorInput(selectedColor);
    setHexColorInput((currentInput) => {
      const normalizedCurrentInput = normalizeHexColor(currentInput);
      const normalizedSelectedColor = normalizeHexColor(selectedColor);

      return normalizedCurrentInput === normalizedSelectedColor &&
        normalizedCurrentInput
        ? currentInput
        : formattedColor;
    });
    setHexColorError('');
  }, [selectedColor]);

  const handleSelectColor = (color: string, inputValue?: string): void => {
    const normalizedColor = normalizeHexColor(color);
    if (!normalizedColor) {
      return;
    }

    onSelectColor(normalizedColor);
    setHexColorInput(inputValue ?? `#${normalizedColor}`);
    setHexColorError('');
  };

  const handleHexColorChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const value = event.target.value.replace(/\s+/g, '');
    const valueWithHash = value && !value.startsWith('#') ? `#${value}` : value;
    const uppercaseValue = valueWithHash.toUpperCase();
    setHexColorInput(uppercaseValue);
    setHexColorError('');

    if (!isCompleteHexInput(uppercaseValue)) {
      return;
    }

    const normalizedColor = normalizeHexColor(uppercaseValue);
    if (!normalizedColor) {
      setHexColorError(INVALID_HEX_COLOR_MESSAGE);
      return;
    }

    handleSelectColor(
      normalizedColor,
      uppercaseValue.length === 4 ? uppercaseValue : undefined
    );
  };

  const handleHexColorBlur = (): void => {
    const isValid =
      isCompleteHexInput(hexColorInput) &&
      Boolean(normalizeHexColor(hexColorInput));
    onValidityChange?.(isValid);

    if (!isValid) {
      setHexColorError(INVALID_HEX_COLOR_MESSAGE);
    }
  };

  const handleCustomColorClick = () => {
    setShowGradient((isVisible) => !isVisible);
  };

  return (
    <div className="color-palette-container">
      <div className="color-palette-grid">
        {colors.map((color) => (
          <div
            className="color-palette-color"
            key={color}
            style={{ backgroundColor: `#${color}` }}
            onClick={() => handleSelectColor(color)}
          >
            {selectedColor === color && (
              <i
                className="fa-solid fa-check"
                style={{ color: 'white', fontSize: '14px' }}
              ></i>
            )}
          </div>
        ))}
      </div>
      <div className="color-palette-custom-container">
        <div
          className="input-field color-palette-custom-label"
          data-testid="tag-group-choose-custom-color"
          onClick={handleCustomColorClick}
          role="button"
          tabIndex={0}
        >
          Choose Custom Color
        </div>

        {showGradient && (
          <>
            <div className="color-palette-custom-input">
              <input
                id="tag-group-custom-color-input"
                data-testid="tag-group-custom-color-input"
                type="text"
                value={hexColorInput}
                maxLength={7}
                placeholder="#FFFFFF"
                aria-invalid={Boolean(hexColorError)}
                aria-describedby={
                  hexColorError ? 'tag-group-custom-color-error' : undefined
                }
                onChange={handleHexColorChange}
                onBlur={handleHexColorBlur}
              />
            </div>
            {hexColorError && (
              <div
                id="tag-group-custom-color-error"
                className="color-palette-custom-error"
                data-testid="tag-group-custom-color-error"
                role="alert"
              >
                {hexColorError}
              </div>
            )}
            <KendoReactColorGradient
              format="hex"
              opacity={false}
              defaultValue={`#${selectedColor}`}
              value={`#${selectedColor}`}
              onChange={handleSelectColor}
              style={{ marginTop: '10px' }}
            />
          </>
        )}
      </div>
    </div>
  );
};
